import { CalendarEvent, CalendarEventColor } from '../../services/calendar'

/**
 * Montagem das grades da agenda (mês, semana e lista).
 *
 * É a porta em TypeScript do que o componente Livewire da agenda do studio faz
 * em PHP (App\Http\Livewire\Criativo\Calendar): mesma altura de hora, mesma
 * faixa mínima de 07h–20h e a mesma distribuição em lanes quando dois eventos
 * se sobrepõem. Só o desenho vive aqui — os eventos vêm prontos do
 * InternalCalendarService.
 */

export const HOUR_HEIGHT = 48

const WEEKDAY_SHORT = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB']
const MONTH_NAME = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
]
const MONTH_SHORT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

// ── Datas ────────────────────────────────────────────────────────────────────
//
// Tudo aqui trabalha com `Y-m-d` em horário local: as datas da agenda são dias
// do calendário, não instantes. `new Date('2026-08-26')` seria interpretado
// como UTC e voltaria um dia atrás nos fusos negativos — daí `parseDate`.

export function parseDate(date: string): Date {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function toDateString(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

/** Domingo da semana da data — a agenda do studio começa a semana no domingo. */
export function startOfWeek(date: Date): Date {
  return addDays(date, -date.getDay())
}

export function isSameDate(a: Date, b: Date): boolean {
  return toDateString(a) === toDateString(b)
}

export function isToday(date: string | Date): boolean {
  const value = typeof date === 'string' ? parseDate(date) : date
  return isSameDate(value, new Date())
}

export function isPastDate(date: string): boolean {
  return parseDate(date) < parseDate(toDateString(new Date()))
}

export function weekdayShort(date: Date): string {
  return WEEKDAY_SHORT[date.getDay()]
}

export function monthLabel(year: number, month: number): string {
  const name = MONTH_NAME[month - 1]
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${year}`
}

export function weekLabel(from: Date, to: Date): string {
  const fromMonth = MONTH_SHORT[from.getMonth()]
  const toMonth = MONTH_SHORT[to.getMonth()]

  if (from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()) {
    return `${from.getDate()} – ${to.getDate()} de ${fromMonth} ${from.getFullYear()}`
  }

  return `${from.getDate()} ${fromMonth} – ${to.getDate()} ${toMonth} ${to.getFullYear()}`
}

/** Ex.: "quarta-feira, 26 de agosto de 2026". */
export function longDateLabel(date: string): string {
  const value = parseDate(date)
  const weekday = value.toLocaleDateString('pt-BR', { weekday: 'long' })
  return `${weekday}, ${value.getDate()} de ${MONTH_NAME[value.getMonth()]} de ${value.getFullYear()}`
}

/** Ex.: "qua, 26 de ago". */
export function shortDateLabel(date: string): string {
  const value = parseDate(date)
  const weekday = value.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '')
  return `${weekday}, ${value.getDate()} de ${MONTH_SHORT[value.getMonth()]}`
}

export function durationLabel(minutes: number | null): string {
  const total = minutes ?? 0
  const hours = Math.floor(total / 60)
  const rest = total % 60
  return rest ? `${hours}h ${rest}min` : `${hours}h`
}

// ── Cores ────────────────────────────────────────────────────────────────────

/** Bloco cheio: célula do mês, chip da semana. */
export function eventChipClass(color: CalendarEventColor): string {
  switch (color) {
    case 'green':
      return 'bg-green-500 text-white'
    case 'red':
      return 'bg-red-500 text-white'
    case 'blue':
      return 'bg-blue-100 text-blue-800 border border-dashed border-blue-400'
    case 'yellow':
      return 'bg-yellow-400 text-slate-800'
    default:
      return 'bg-slate-400 text-white'
  }
}

/** Cartão claro: painel lateral e visão de agenda. */
export function eventCardClass(color: CalendarEventColor): string {
  switch (color) {
    case 'green':
      return 'border-green-200 bg-green-50'
    case 'red':
      return 'border-red-200 bg-red-50'
    case 'blue':
      return 'border-dashed border-blue-200 bg-blue-50'
    case 'yellow':
      return 'border-yellow-200 bg-yellow-50'
    default:
      return 'border-slate-200 bg-slate-50'
  }
}

export function eventBarClass(color: CalendarEventColor): string {
  switch (color) {
    case 'green':
      return 'bg-green-500'
    case 'red':
      return 'bg-red-500'
    case 'blue':
      return 'bg-blue-500'
    case 'yellow':
      return 'bg-yellow-500'
    default:
      return 'bg-slate-500'
  }
}

// ── Agrupamento ──────────────────────────────────────────────────────────────

export function groupByDate(events: CalendarEvent[]): Record<string, CalendarEvent[]> {
  return events.reduce<Record<string, CalendarEvent[]>>((groups, event) => {
    ;(groups[event.date] ||= []).push(event)
    return groups
  }, {})
}

function timeToMinutes(time: string | null): number {
  if (!time || !time.includes(':')) {
    return 0
  }
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

// ── Visão: mês ───────────────────────────────────────────────────────────────

export interface MonthDay {
  date: string
  day: number
  isCurrentMonth: boolean
  isToday: boolean
  events: CalendarEvent[]
}

export function buildMonthDays(year: number, month: number, events: CalendarEvent[]): MonthDay[] {
  const byDate = groupByDate(events)
  const firstDay = new Date(year, month - 1, 1)
  const lastDay = new Date(year, month, 0)

  const cursor = startOfWeek(firstDay)
  const endGrid = addDays(startOfWeek(lastDay), 6)

  const days: MonthDay[] = []
  for (let day = new Date(cursor); day <= endGrid; day = addDays(day, 1)) {
    const date = toDateString(day)
    days.push({
      date,
      day: day.getDate(),
      isCurrentMonth: day.getMonth() === month - 1,
      isToday: isToday(day),
      events: byDate[date] ?? [],
    })
  }

  return days
}

// ── Visão: semana ────────────────────────────────────────────────────────────

export interface PositionedEvent {
  event: CalendarEvent
  top: number
  height: number
  leftPct: number
  widthPct: number
}

export interface WeekDay {
  date: string
  weekday: string
  day: number
  isToday: boolean
  allDayEvents: CalendarEvent[]
  events: PositionedEvent[]
}

export interface WeekData {
  days: WeekDay[]
  hours: string[]
  gridHeight: number
  hasAllDay: boolean
}

/** Sete colunas a partir do domingo. */
export function buildWeekData(weekStart: string, events: CalendarEvent[]): WeekData {
  return buildTimeGrid(weekStart, 7, events)
}

/** A mesma grade de horas com uma coluna só — a visão de dia. */
export function buildDayData(date: string, events: CalendarEvent[]): WeekData {
  return buildTimeGrid(date, 1, events)
}

/**
 * Grade de horas de N dias consecutivos. Dia e semana são a mesma tela com
 * larguras diferentes, então dividem a faixa de horas e o cálculo de lanes.
 */
function buildTimeGrid(startDate: string, dayCount: number, events: CalendarEvent[]): WeekData {
  const byDate = groupByDate(events)

  // Faixa exibida: 07h–20h por padrão, esticando para caber o que houver fora.
  let minStart = 7 * 60
  let maxEnd = 20 * 60
  events.forEach(event => {
    if (event.is_flexible || !event.starts_at) {
      return
    }
    minStart = Math.min(minStart, timeToMinutes(event.starts_at))
    maxEnd = Math.max(maxEnd, timeToMinutes(event.ends_at ?? event.starts_at))
  })

  const startHour = Math.max(0, Math.floor(minStart / 60))
  let endHour = Math.min(24, Math.ceil(maxEnd / 60))
  if (endHour <= startHour) {
    endHour = startHour + 1
  }

  const from = parseDate(startDate)
  const days: WeekDay[] = []

  for (let i = 0; i < dayCount; i++) {
    const cursor = addDays(from, i)
    const date = toDateString(cursor)
    const allDayEvents: CalendarEvent[] = []
    const timed: CalendarEvent[] = []

    ;(byDate[date] ?? []).forEach(event => {
      if (event.is_flexible || !event.starts_at) {
        allDayEvents.push(event)
      } else {
        timed.push(event)
      }
    })

    days.push({
      date,
      weekday: weekdayShort(cursor),
      day: cursor.getDate(),
      isToday: isToday(cursor),
      allDayEvents,
      events: layoutDayLanes(timed, startHour),
    })
  }

  const hours: string[] = []
  for (let hour = startHour; hour < endHour; hour++) {
    hours.push(`${`${hour}`.padStart(2, '0')}:00`)
  }

  return {
    days,
    hours,
    gridHeight: (endHour - startHour) * HOUR_HEIGHT,
    hasAllDay: days.some(day => day.allDayEvents.length > 0),
  }
}

/**
 * Distribui os eventos de um dia em colunas para não se sobreporem. Um evento
 * entra na primeira coluna já livre no horário dele; se não houver, abre outra
 * e todas encolhem — é o comportamento do Google Calendar e o do studio.
 */
function layoutDayLanes(events: CalendarEvent[], startHour: number): PositionedEvent[] {
  const bounded = events
    .map(event => {
      const startMin = timeToMinutes(event.starts_at)
      return {
        event,
        startMin,
        // Piso de 15 min: um evento de poucos minutos ficaria invisível na grade.
        endMin: Math.max(startMin + 15, timeToMinutes(event.ends_at ?? event.starts_at)),
      }
    })
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin)

  const laneEnds: number[] = []
  const lanes = bounded.map(item => {
    let lane = laneEnds.findIndex(end => item.startMin >= end)
    if (lane === -1) {
      lane = laneEnds.length
    }
    laneEnds[lane] = item.endMin
    return { ...item, lane }
  })

  const laneCount = Math.max(1, laneEnds.length)
  const offset = startHour * 60

  return lanes.map(item => ({
    event: item.event,
    top: ((item.startMin - offset) / 60) * HOUR_HEIGHT,
    height: Math.max(22, ((item.endMin - item.startMin) / 60) * HOUR_HEIGHT),
    widthPct: 100 / laneCount,
    leftPct: (item.lane / laneCount) * 100,
  }))
}

// ── Visão: agenda (lista) ────────────────────────────────────────────────────

export interface ListGroup {
  date: string
  weekday: string
  day: number
  isToday: boolean
  isPast: boolean
  events: CalendarEvent[]
}

export function buildListGroups(events: CalendarEvent[]): ListGroup[] {
  const byDate = groupByDate(events)

  return Object.keys(byDate)
    .sort()
    .map(date => {
      const value = parseDate(date)
      return {
        date,
        weekday: value.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', ''),
        day: value.getDate(),
        isToday: isToday(value),
        isPast: isPastDate(date) && !isToday(value),
        events: byDate[date],
      }
    })
}
